/**
 * Fleet access control helpers
 * Checks if a user has access to fleet features based on their fleetRole
 */

export type FleetRole = 'FLEET_ADMIN' | 'FLEET_MANAGER' | 'FLEET_DRIVER' | 'FLEET_VIEWER';

export interface FleetUser {
  fleetRole?: FleetRole | null;
  isFleetManager?: boolean; // Legacy
  role?: string; // Global role (ADMIN, USER)
  fleetMemberSince?: string | Date | null;
  invitedById?: string | null;
}

/**
 * Check if user has any fleet access (any fleet role, legacy manager, admin, or member)
 */
export function hasFleetAccess(user: FleetUser | null | undefined): boolean {
  if (!user) return false;
  
  // Check new fleetRole system first
  if (user.fleetRole && ['FLEET_ADMIN', 'FLEET_MANAGER', 'FLEET_DRIVER', 'FLEET_VIEWER'].includes(user.fleetRole)) {
    return true;
  }
  
  // Legacy checks for backward compatibility
  if (user.isFleetManager === true) return true;
  if (user.role === 'ADMIN') return true; // Global admin
  if (user.fleetMemberSince || user.invitedById) return true;
  
  return false;
}

/**
 * Check if user has fleet admin access (can manage everything)
 */
export function isFleetAdmin(user: FleetUser | null | undefined): boolean {
  if (!user) return false;
  if (user.fleetRole === 'FLEET_ADMIN') return true;
  // Legacy: global admin or fleet manager
  if (user.role === 'ADMIN' || user.isFleetManager === true) return true;
  return false;
}

/**
 * Check if user can manage fleet (admin or manager)
 */
export function canManageFleet(user: FleetUser | null | undefined): boolean {
  if (!user) return false;
  if (user.fleetRole === 'FLEET_ADMIN' || user.fleetRole === 'FLEET_MANAGER') return true;
  // Legacy: global admin or fleet manager
  if (user.role === 'ADMIN' || user.isFleetManager === true) return true;
  return false;
}

/**
 * Check if user can view fleet (any role including viewer)
 */
export function canViewFleet(user: FleetUser | null | undefined): boolean {
  return hasFleetAccess(user);
}

/**
 * Check if user can assign jobs (admin, manager, or driver)
 */
export function canAssignJobs(user: FleetUser | null | undefined): boolean {
  if (!user) return false;
  if (user.fleetRole === 'FLEET_ADMIN' || user.fleetRole === 'FLEET_MANAGER' || user.fleetRole === 'FLEET_DRIVER') return true;
  // Legacy: global admin or fleet manager
  if (user.role === 'ADMIN' || user.isFleetManager === true) return true;
  return false;
}

/**
 * Check if user can view analytics (admin, manager, or viewer)
 */
export function canViewAnalytics(user: FleetUser | null | undefined): boolean {
  if (!user) return false;
  if (user.fleetRole === 'FLEET_ADMIN' || user.fleetRole === 'FLEET_MANAGER' || user.fleetRole === 'FLEET_VIEWER') return true;
  // Legacy: global admin or fleet manager
  if (user.role === 'ADMIN' || user.isFleetManager === true) return true;
  return false;
}

/**
 * Check if user can manage billing/subscriptions (admin only)
 */
export function canManageBilling(user: FleetUser | null | undefined): boolean {
  return isFleetAdmin(user);
}

